import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

interface PendingProfile {
  id: string;
  user_id: string;
  full_name: string | null;
  role: "CEO" | "Advogado" | "Secretário(a)" | "Estagiário" | null;
  status: "PENDING" | "ACTIVE" | "INACTIVE" | null;
  created_at: string;
}

const UserApprovals = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState<PendingProfile[]>([]);
  const fetchPending = async () => {
    setLoading(true);
    try {
      console.log('🔍 UserApprovals: Iniciando busca de perfis pendentes...');
      
      // Usar apenas Supabase diretamente (políticas RLS já corrigidas)
      const { data, error } = await supabase
        .from("profiles")
        .select("id, user_id, full_name, role, status, created_at")
        .eq("status", "PENDING")
        .order("created_at", { ascending: false });
      
      console.log('🔍 UserApprovals: Resultado da consulta:', { data, error });
      
      if (error) {
        console.error("❌ UserApprovals: Erro Supabase:", error);
        throw error;
      }
      
      console.log('✅ UserApprovals: Perfis encontrados:', data?.length || 0);
      setProfiles((data || []) as PendingProfile[]);
    } catch (err: any) {
      console.error("❌ UserApprovals: Erro ao carregar perfis pendentes:", err);
      toast({
        title: "Erro",
        description: err.message || "Não foi possível carregar contas pendentes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🔍 UserApprovals: useEffect executado');
    console.log('🔍 UserApprovals: Profile atual:', profile);
    console.log('🔍 UserApprovals: Profile role:', profile?.role);
    console.log('🔍 UserApprovals: Profile status:', profile?.status);
    
    if (profile?.role !== "CEO") {
      console.log('❌ UserApprovals: Usuário não é CEO, não executando fetchPending');
      return;
    }
    
    console.log('✅ UserApprovals: Usuário é CEO, executando fetchPending');
    fetchPending();
  }, [profile]);

  const handleApprove = async (id: string) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ status: "ACTIVE" })
        .eq("id", id);
      
      if (error) throw error;
      
      toast({ title: "Aprovado", description: "Conta ativada com sucesso" });
      fetchPending();
    } catch (err: any) {
      console.error("Erro ao aprovar:", err);
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    }
  };

  const handleReject = async (id: string) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ status: "INACTIVE" })
        .eq("id", id);
      
      if (error) throw error;
      
      toast({ title: "Rejeitado", description: "Conta marcada como inativa" });
      fetchPending();
    } catch (err: any) {
      console.error("Erro ao rejeitar:", err);
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    }
  };

  if (profile?.role !== "CEO") {
    console.log('❌ UserApprovals: Usuário não é CEO');
    console.log('❌ UserApprovals: Profile role:', profile?.role);
    console.log('❌ UserApprovals: Profile completo:', profile);
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>Permissão necessária</CardTitle>
          </CardHeader>
          <CardContent>
            Você não tem permissão para acessar aprovações de usuários.
            <br />
            <strong>Role atual:</strong> {profile?.role || 'Não definido'}
            <br />
            <strong>Status:</strong> {profile?.status || 'Não definido'}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-5xl">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold">Aprovação de Usuários</h1>
        <p className="text-muted-foreground">
          Contas aguardando análise do administrador
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Contas Pendentes</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>Carregando...</p>
          ) : profiles.length === 0 ? (
            <p className="text-muted-foreground">Nenhuma conta pendente no momento.</p>
          ) : (
            <div className="space-y-3">
              {profiles.map((p) => (
                <div key={p.id} className="flex items-center justify-between border rounded-md p-3">
                  <div>
                    <p className="font-medium">
                      {p.full_name || "(Sem nome)"}
                    </p>
                    <p className="text-xs text-muted-foreground">User ID: {p.user_id}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <Badge variant="secondary">{p.role || "Sem função"}</Badge>
                      <Badge variant="outline">{p.status}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleApprove(p.id)}>
                      Aprovar
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-500 text-red-600 hover:bg-red-50" onClick={() => handleReject(p.id)}>
                      Rejeitar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserApprovals;